// Shared rules for the /[lang]/boat-rental/[city] landing pages. Framework-free
// so the build-time prefetch (list-prefetch.ts), generateStaticParams and the
// screen all apply the same threshold and compute the same numbers.

import { boatPortPretty, type Boat } from '@todo/core/boats';
import { cities } from '@/lib/events-data';
import { BOAT_PRICES, BOAT_TYPES, type BoatTypeFilter } from '@todo/core/hub-filters';

/**
 * A city gets its own /boat-rental/[city] page only with at least this many
 * boats — below it the page would be thin content. The same floor makes a page
 * `noindex` if it ever renders with fewer (e.g. the catalogue shrinks).
 */
export const MIN_BOATS_PER_CITY_PAGE = 5;

/** How many full cards the "top-rated" grid shows; every boat is still linked from the list below it. */
export const TOP_BOATS_ON_CITY_PAGE = 12;

/** City ids (in `cities` order) with enough boats for their own page. */
export function boatRentalCityIds(boats: Pick<Boat, 'city_id'>[]): string[] {
  const counts = new Map<string, number>();
  for (const boat of boats) {
    if (boat.city_id) counts.set(boat.city_id, (counts.get(boat.city_id) ?? 0) + 1);
  }
  return cities
    .map((city) => city.id)
    .filter((id) => (counts.get(id) ?? 0) >= MIN_BOATS_PER_CITY_PAGE);
}

export type BoatTypeStats = {
  /** Raw `boats.boat_type` value (e.g. "Motorboat"), localized at render time. */
  type: string;
  count: number;
  minPrice: number | null;
  medianPrice: number | null;
};

export type BoatCityStats = {
  total: number;
  minPrice: number | null;
  medianPrice: number | null;
  avgRating: number | null;
  /** Most common type first. */
  types: BoatTypeStats[];
  /** Departure ports parsed from the booking URLs, most boats first. Empty when the rows carry no booking_url. */
  ports: { name: string; count: number }[];
};

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return Math.round(sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2);
}

function priceStats(boats: Boat[]) {
  const prices = boats.map((b) => b.from_price).filter((p): p is number => p != null);
  return {
    minPrice: prices.length ? Math.round(Math.min(...prices)) : null,
    medianPrice: median(prices),
  };
}

/** Section key for a boat with no `boat_type`. */
export const OTHER_BOAT_TYPE = 'other';

/** Group key for a boat's section on the city page. */
export function boatTypeKey(boat: Pick<Boat, 'boat_type'>): string {
  return boat.boat_type ?? OTHER_BOAT_TYPE;
}

/**
 * The numbers the city page is built from — counts and prices per boat type,
 * departure ports. Computed at export time from the full rows (the baked card
 * seed drops `booking_url`, which the ports come from) and baked separately;
 * the screen only recomputes it from its own rows when there's no seed.
 */
export function boatCityStats(boats: Boat[]): BoatCityStats {
  const byType = new Map<string, Boat[]>();
  for (const boat of boats) {
    const key = boatTypeKey(boat);
    const rows = byType.get(key);
    if (rows) rows.push(boat);
    else byType.set(key, [boat]);
  }
  const types = [...byType.entries()]
    .map(([type, rows]) => ({ type, count: rows.length, ...priceStats(rows) }))
    .sort((a, b) => b.count - a.count || a.type.localeCompare(b.type));

  const portCounts = new Map<string, number>();
  for (const boat of boats) {
    const port = boatPortPretty(boat.booking_url);
    if (port) portCounts.set(port, (portCounts.get(port) ?? 0) + 1);
  }
  const ports = [...portCounts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  const ratings = boats.map((b) => b.rating).filter((r): r is number => r != null);
  const avgRating = ratings.length
    ? Math.round((ratings.reduce((sum, r) => sum + r, 0) / ratings.length) * 10) / 10
    : null;

  return { total: boats.length, ...priceStats(boats), avgRating, types, ports };
}

// ── /[lang]/boat-rental/[city]/[type] ──────────────────────────────────────
// One boat type in one city, e.g. /fr/boat-rental/cannes/catamaran — the page
// for "location catamaran cannes" / "bateau sans permis antibes" searches,
// which Search Console showed landing on individual boat pages at position
// 60–80 because no page matched them.

/**
 * URL segment of each type page → the hub filter bucket it lists, so a type
 * page shows exactly what the city page's type filter would (e.g. `yacht` is
 * motor and sailing yachts). Segments are language-neutral, like city ids.
 */
export const BOAT_TYPE_PAGES = {
  motorboat: 'motorboat',
  rib: 'rib',
  yacht: 'yacht',
  sailboat: 'sailboat',
  catamaran: 'catamaran',
  'no-licence': 'nolicence',
} as const satisfies Record<string, Exclude<BoatTypeFilter, 'any'>>;

export type BoatTypePage = keyof typeof BOAT_TYPE_PAGES;

const TYPE_PAGE_KEYS = Object.keys(BOAT_TYPE_PAGES) as BoatTypePage[];

export function isBoatTypePage(value: string | undefined): value is BoatTypePage {
  return value != null && Object.prototype.hasOwnProperty.call(BOAT_TYPE_PAGES, value);
}

/** The raw `boats.boat_type` values a type page lists. */
export function boatTypePageRawTypes(type: BoatTypePage): string[] {
  return BOAT_TYPES[BOAT_TYPE_PAGES[type]] ?? [];
}

/** The type page a raw `boat_type` belongs to, if any (jet skis, houseboats have none). */
export function boatTypePageOf(rawType: string | null | undefined): BoatTypePage | undefined {
  if (!rawType) return undefined;
  return TYPE_PAGE_KEYS.find((type) => boatTypePageRawTypes(type).includes(rawType));
}

export function boatsOfTypePage<B extends Pick<Boat, 'boat_type'>>(boats: B[], type: BoatTypePage): B[] {
  const raw = boatTypePageRawTypes(type);
  return boats.filter((boat) => boat.boat_type != null && raw.includes(boat.boat_type));
}

/**
 * A city × type page needs at least this many boats — fewer and it would say
 * nothing the city page doesn't. As with MIN_BOATS_PER_CITY_PAGE, the same
 * floor makes a page `noindex` if it ever renders with fewer.
 */
export const MIN_BOATS_PER_TYPE_PAGE = 8;

/** City id → its type pages, most boats first. Only cities with a city page. */
export type BoatTypePages = Record<string, BoatTypePage[]>;

export function boatTypePages(boats: Pick<Boat, 'city_id' | 'boat_type'>[]): BoatTypePages {
  const pages: BoatTypePages = {};
  for (const cityId of boatRentalCityIds(boats)) {
    const inCity = boats.filter((boat) => boat.city_id === cityId);
    const counts = TYPE_PAGE_KEYS.map((type) => ({ type, count: boatsOfTypePage(inCity, type).length }))
      .filter((entry) => entry.count >= MIN_BOATS_PER_TYPE_PAGE)
      .sort((a, b) => b.count - a.count);
    if (counts.length > 0) pages[cityId] = counts.map((entry) => entry.type);
  }
  return pages;
}

export type BoatPriceBand = { min?: number; max?: number; count: number };

/**
 * How many boats fall in each of the hub's price buckets (BOAT_PRICES) — the
 * type page's price table. Empty buckets are dropped.
 */
export function boatPriceBands(boats: Pick<Boat, 'from_price'>[]): BoatPriceBand[] {
  return Object.entries(BOAT_PRICES)
    .filter(([key]) => key !== 'any')
    .map(([, { min, max }]) => ({
      min,
      max,
      count: boats.filter(
        (boat) =>
          boat.from_price != null &&
          (min == null || boat.from_price >= min) &&
          (max == null || boat.from_price < max),
      ).length,
    }))
    .filter((band) => band.count > 0);
}
